import { LaserficheMultiConnectionManager } from "./LaserficheMultiConnectionManager";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { SettingsIcon } from "lucide-react";
import { useTeamList } from "@/hooks/queryHooks/teamHooks";
import { useLaserficheListConnections } from "@/hooks/queryHooks/laserficheIntegrationHooks";
import { laserficheIntegrationRepo } from "@/repositories/laserficheIntegrationRepo";

export interface LaserficheMultiConnectionEditorProps {
  tenantId: string;
}

export const LaserficheMultiConnectionEditor = ({
  tenantId,
}: LaserficheMultiConnectionEditorProps) => {
  const { data: teams } = useTeamList(tenantId);
  const { data: connections } = useLaserficheListConnections(tenantId);

  const navigate = useNavigate();

  const handleEdit = (tenantId: string, teamId?: string) => {
    navigate(
      new AppUrlPath(tenantId).getIntegrationPath(
        "laserfiche",
        teamId ?? "default"
      )
    );
  };
  const handleConnect = (tenantId: string, teamId?: string) => {
    navigate(
      new AppUrlPath(tenantId).getIntegrationPath(
        "laserfiche",
        teamId ?? "default"
      ) + "#new=true"
    );
  };
  const handleDisconnect = async (tenantId: string, teamId?: string) => {
    await laserficheIntegrationRepo.deleteConnection(tenantId, teamId ?? null);
  };

  if (!teams || !connections) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p>
        If your organization only needs to connect to one Laserfiche account
        with one user login, use the "Default Connection".
      </p>
      <p>
        However, if you have multiple Laserfiche accounts, or if different teams
        will require different user logins (in cases when certain users have
        limited permissions), you may need to configure multiple Laserfiche
        connections. In this case, make the necessary connections below.
      </p>
      <p>
        You may also configure a default connection, and then per-team
        connections. Any teams without specific connections will fall back to
        using the default connection.
      </p>
      <p>
        <strong>
          To return to this screen, click{" "}
          <em className="inline-block mt-[1px] mx-1">
            <SettingsIcon className="w-4 inline align-middle" /> Configuration
          </em>{" "}
          at the top-right of the Laserfiche integration configuration screen.
        </strong>
      </p>

      <LaserficheMultiConnectionManager
        teams={teams}
        tenantId={tenantId}
        connections={connections}
        onEdit={handleEdit}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
      />
    </div>
  );
};
